import { useState } from "react";
import { Box, Typography, Paper, TextField, MenuItem, Alert, Chip, Button, Tooltip } from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxNotificationDto, type DxNotificationStatus } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { formatDateTime } from "@/shared/datetime";

const STATUSES: ("" | DxNotificationStatus)[] = ["", "Pending", "Sent", "Failed", "Dead"];
const statusColor = (s: string) => s === "Sent" ? "success" : s === "Failed" ? "warning" : s === "Dead" ? "error" : "default";

export function DiagnosticsNotificationsPage() {
  const qc = useQueryClient();
  const canResend = useAuth(s => s.hasPermission("diagnostics.notification.resend"));
  const [status, setStatus] = useState<"" | DxNotificationStatus>("");
  const query = useQuery({
    queryKey: ["dx-notifications", status],
    queryFn: () => diagnosticsApi.listNotifications({ status: status || undefined }),
  });
  const resend = useMutation({
    meta: { successMessage: "Notification re-queued" },
    mutationFn: (id: string) => diagnosticsApi.resendNotification(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dx-notifications"] }),
  });

  const columns: GridColDef<DxNotificationDto>[] = [
    { field: "queuedAt", headerName: "Queued", width: 165, valueGetter: v => v ? formatDateTime(v as string) : "" },
    { field: "eventType", headerName: "Event", width: 150 },
    { field: "recipientType", headerName: "Recipient", width: 130 },
    { field: "channel", headerName: "Channel", width: 100 },
    { field: "message", headerName: "Message", flex: 1, minWidth: 220 },
    { field: "attemptCount", headerName: "Tries", width: 70, align: "right", headerAlign: "right" },
    { field: "status", headerName: "Status", width: 110, renderCell: p => <Chip size="small" label={p.row.status} color={statusColor(p.row.status)} variant="outlined" /> },
    {
      field: "actions", headerName: "", width: 110, sortable: false, align: "right", renderCell: p =>
        (p.row.status === "Failed" || p.row.status === "Dead") && canResend
          ? <Button size="small" disabled={resend.isPending} onClick={() => resend.mutate(p.row.id)}>Resend</Button>
          : p.row.lastError ? <Tooltip title={p.row.lastError}><Chip size="small" label="error" color="warning" variant="outlined" /></Tooltip> : null,
    },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Diagnostics"]} title="Notifications" />
      <Paper sx={{ p: 2 }}>
        <TextField select label="Status" size="small" value={status} onChange={e => setStatus(e.target.value as "" | DxNotificationStatus)} sx={{ width: 200, mb: 2 }}>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
        </TextField>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {resend.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(resend.error)}</Alert>}
        <Box sx={{ height: 560 }}>
          <DataGrid rows={query.data ?? []} columns={columns} loading={query.isLoading} getRowId={r => r.id}
            disableRowSelectionOnClick pageSizeOptions={[25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Outbound alerts (critical value, sample rejection / recollection, result release) are sent via the external communication gateway. Failed deliveries retry automatically up to 4 times; after that they can be resent manually.
        </Typography>
      </Paper>
    </Box>
  );
}
